import React, { useState, useEffect, useCallback, useMemo } from "react";
import SearchableSelect from "./SearchableSelect";
import {
  fetchBaseWorkRequirements,
  createBaseWorkRequirement,
  updateBaseWorkRequirement,
  deleteBaseWorkRequirement,
  fetchTier1Catalogs,
  fetchTier1BaseWorks,
} from "../api/tier1Api";
import {
  fetchClassifications,
  fetchProducts,
  fetchUnits,
  fetchOfferHistory,
  createOffer,
  supersedeOffer,
  fetchSuppliers,
} from "../api/resourceCatalogApi";
import { confirmDialog } from "../utils/confirmDialog";

const KINDS = [
  { id: "MATERIAL", label: "მასალა / Material", color: "#4CAF50" },
  { id: "EQUIPMENT", label: "მექანიზმი / Equipment", color: "#FF9800" },
  { id: "LABOUR", label: "ხელფასი / Labour", color: "#2196F3" },
];

const KIND_COLOR = Object.fromEntries(KINDS.map((k) => [k.id, k.color]));
const KIND_LETTER = { MATERIAL: "M", EQUIPMENT: "E", LABOUR: "L" };

/**
 * Modal that lists + edits all `base_work_requirements` for one base work.
 * A requirement points to EITHER a classification (generic) OR a specific
 * product, with quantity per base unit of the work.
 */
export default function BaseWorkRequirementsModal({
  token, baseWork, tier = 1, isSuperuser = false, onClose,
}) {
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [units, setUnits] = useState([]);
  const [editing, setEditing] = useState(null);  // null = no form open, "new" = create, number = edit existing id
  const [copyFromOpen, setCopyFromOpen] = useState(false);

  const load = useCallback(async () => {
    if (!baseWork) return;
    setBusy(true); setError("");
    try {
      const list = await fetchBaseWorkRequirements(token, baseWork.id, tier);
      setReqs(list || []);
    } catch (e) {
      setError(e?.message || "Failed to load requirements");
    } finally {
      setBusy(false);
    }
  }, [token, baseWork, tier]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchUnits(token).then((list) => setUnits(list || [])).catch(() => setUnits([]));
  }, [token]);

  const handleDelete = async (req) => {
    if (!(await confirmDialog("Delete this requirement?"))) return;
    setBusy(true); setError("");
    try {
      await deleteBaseWorkRequirement(token, baseWork.id, req.id, tier);
      load();
    } catch (e) {
      setError(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!baseWork) return null;

  const groupedByKind = useMemo(() => {
    const g = { MATERIAL: [], EQUIPMENT: [], LABOUR: [] };
    reqs.forEach((r) => (g[r.requirement_kind] || (g[r.requirement_kind] = [])).push(r));
    return g;
  }, [reqs]);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1100,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: "#0f172a", border: "1px solid #334155", borderRadius: 6,
        padding: 16, width: "100%", maxWidth: 720, maxHeight: "90vh", overflow: "auto",
        position: "relative",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 14 }}>Requirements for base work</h3>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2, fontFamily: "monospace" }}>
              {baseWork.code} · {baseWork.name_geo || baseWork.name_eng || ""}
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              Base unit: {baseWork.base_unit_value || 1} {baseWork.base_unit || ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {isSuperuser && (
              <button className="boq-action-btn" onClick={() => setCopyFromOpen(true)} disabled={busy}
                title="Copy requirements from another base work">
                ⎘ Copy from...
              </button>
            )}
            <button className="boq-action-btn" onClick={onClose} disabled={busy}>✕ Close</button>
          </div>
        </div>

        {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}

        {/* Requirements grouped by kind */}
        {KINDS.map((kind) => (
          <div key={kind.id} className="panel" style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
              <span style={{
                background: kind.color, color: "#fff", fontSize: 10,
                padding: "1px 6px", borderRadius: 3, marginRight: 6, fontWeight: 700,
              }}>{kind.id[0]}</span>
              <strong style={{ fontSize: 12, flex: 1 }}>{kind.label}</strong>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>{groupedByKind[kind.id]?.length || 0}</span>
            </div>
            {(groupedByKind[kind.id] || []).map((r) => (
              <RequirementRow
                key={r.id}
                req={r}
                token={token}
                isSuperuser={isSuperuser}
                onEdit={() => setEditing(r.id)}
                onDelete={() => handleDelete(r)}
                busy={busy}
              />
            ))}
            {(groupedByKind[kind.id] || []).length === 0 && (
              <div style={{ fontSize: 11, color: "#64748b", padding: "4px 6px" }}>
                No {kind.label.split(" / ")[1].toLowerCase()} requirements yet.
              </div>
            )}
          </div>
        ))}

        {/* Add / Edit form */}
        {isSuperuser && (
          <>
            {editing === null && (
              <button className="primary" style={{ width: "100%" }}
                onClick={() => setEditing("new")} disabled={busy}>
                + Add requirement
              </button>
            )}
            {editing !== null && (
              <RequirementEditForm
                token={token}
                baseWorkId={baseWork.id}
                tier={tier}
                units={units}
                existingReq={editing === "new" ? null : reqs.find((r) => r.id === editing)}
                onCancel={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
                setError={setError}
              />
            )}
          </>
        )}
      </div>

      {/* Copy-from panel (nested overlay inside the modal) */}
      {copyFromOpen && (
        <CopyFromPanel
          token={token}
          targetBaseWork={baseWork}
          tier={tier}
          onClose={() => setCopyFromOpen(false)}
          onCopied={() => { setCopyFromOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function RequirementRow({ req, token, isSuperuser, onEdit, onDelete, busy }) {
  const [open, setOpen] = useState(false);
  const name = req.product_id
    ? (req.product_name_geo || req.product_name_eng || req.product_display_id || `product #${req.product_id}`)
    : (req.classification_name_geo || req.classification_name_eng || req.classification_code);
  const subLabel = req.product_id
    ? `product · ${req.product_display_id || "—"}${req.product_is_generic ? " (generic)" : ""}`
    : `generic class · ${req.classification_code}`;
  const unit = req.unit_code || req.product_base_unit || "—";
  const canShowPrices = Boolean(req.product_id); // only specific products have offers

  return (
    <div style={{ borderBottom: "1px dotted #334155" }}>
      <div style={{
        display: "flex", alignItems: "center", padding: "4px 6px",
        fontSize: 12,
      }}>
        {canShowPrices && (
          <button onClick={() => setOpen(!open)}
            style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 11, marginRight: 4, padding: 0, width: 12 }}>
            {open ? "▾" : "▸"}
          </button>
        )}
        {!canShowPrices && <span style={{ width: 16 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
          <div style={{ fontSize: 10, color: "#94a3b8" }}>{subLabel}</div>
        </div>
        <div style={{ textAlign: "right", paddingLeft: 8, minWidth: 110 }}>
          <span style={{ fontWeight: 600 }}>{req.quantity_per_base_unit}</span>
          <span style={{ color: "#94a3b8" }}> {unit}</span>
        </div>
        {isSuperuser && (
          <>
            <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 11, marginLeft: 6 }}
              onClick={onEdit} disabled={busy}>edit</button>
            <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 11, marginLeft: 4 }}
              onClick={onDelete} disabled={busy}>✕</button>
          </>
        )}
      </div>
      {open && canShowPrices && (
        <ProductPriceSection
          token={token}
          productId={req.product_id}
          isSuperuser={isSuperuser}
        />
      )}
    </div>
  );
}

// ── Inline price section for a product: active offers + history + supersede ─

function ProductPriceSection({ token, productId, isSuperuser }) {
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [supersedingId, setSupersedingId] = useState(null);
  const [newRate, setNewRate] = useState("");
  const [newReason, setNewReason] = useState("");
  const [addingFresh, setAddingFresh] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [freshOffer, setFreshOffer] = useState({ supplier_id: "", unit_rate: "", currency: "GEL" });

  const reload = useCallback(async () => {
    try {
      const list = await fetchOfferHistory(token, { productId });
      setHistory(list || []);
    } catch (e) {
      setError(e?.message || "Failed to load price history");
    }
  }, [token, productId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    fetchSuppliers(token, { isActive: true }).then((list) => setSuppliers(list || [])).catch(() => setSuppliers([]));
  }, [token]);

  const active = history.filter((h) => h.is_active);
  const past = history.filter((h) => !h.is_active);

  const handleSupersede = async (offerId) => {
    if (!newRate) return setError("New rate is required.");
    setBusy(true); setError("");
    try {
      await supersedeOffer(token, offerId, {
        new_unit_rate: Number(newRate),
        change_reason: newReason || null,
      });
      setSupersedingId(null); setNewRate(""); setNewReason("");
      reload();
    } catch (e) {
      setError(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleAddFresh = async () => {
    if (!freshOffer.supplier_id || !freshOffer.unit_rate) return setError("Pick a supplier and enter a rate.");
    setBusy(true); setError("");
    try {
      await createOffer(token, {
        supplier_id: Number(freshOffer.supplier_id),
        product_id: productId,
        unit_rate: Number(freshOffer.unit_rate),
        currency: freshOffer.currency || "GEL",
      });
      setAddingFresh(false);
      setFreshOffer({ supplier_id: "", unit_rate: "", currency: "GEL" });
      reload();
    } catch (e) {
      setError(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginLeft: 22, marginRight: 6, marginBottom: 6, padding: 8,
      background: "rgba(15,23,42,0.6)", borderRadius: 4, border: "1px solid #1e293b",
    }}>
      {error && <div style={{ color: "#f87171", fontSize: 11, marginBottom: 4 }}>{error}</div>}

      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>
        Active supplier offers ({active.length})
      </div>
      {active.length === 0 && (
        <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
          No active prices. {isSuperuser && "Add one below."}
        </div>
      )}
      {active.map((o) => (
        <div key={o.id} style={{ marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", fontSize: 11, gap: 4 }}>
            <span style={{ flex: 1 }}>
              {o.is_preferred && <span style={{ color: "#34d399" }}>★ </span>}
              <strong>{o.supplier_name}</strong>
            </span>
            <span style={{ fontWeight: 600, color: "#67e8f9" }}>{o.unit_rate} {o.currency}</span>
            <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: 4 }}>since {o.valid_from}</span>
            {isSuperuser && (
              <button className="boq-action-btn" style={{ padding: "1px 6px", fontSize: 10, marginLeft: 4 }}
                onClick={() => { setSupersedingId(o.id); setNewRate(""); setNewReason(""); }}
                disabled={busy}>
                update price
              </button>
            )}
          </div>
          {supersedingId === o.id && (
            <div style={{
              marginTop: 4, padding: 6, background: "rgba(20,184,166,0.08)",
              border: "1px solid #14b8a6", borderRadius: 3,
              display: "grid", gap: 4,
            }}>
              <div style={{ fontSize: 10, color: "#94a3b8" }}>
                ⚠ The current price ({o.unit_rate} {o.currency} since {o.valid_from}) will be expired today;
                the new price starts today. History is preserved.
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <input type="number" placeholder="new rate" value={newRate}
                  onChange={(e) => setNewRate(e.target.value)} style={{ flex: 1 }} />
                <span style={{ fontSize: 11, alignSelf: "center", color: "#94a3b8" }}>{o.currency}</span>
              </div>
              <input placeholder="reason for change (optional)" value={newReason}
                onChange={(e) => setNewReason(e.target.value)} />
              <div style={{ display: "flex", gap: 4 }}>
                <button className="boq-action-btn" style={{ flex: 1, fontSize: 11 }}
                  onClick={() => setSupersedingId(null)} disabled={busy}>Cancel</button>
                <button className="primary" style={{ flex: 2, fontSize: 11 }}
                  onClick={() => handleSupersede(o.id)} disabled={busy || !newRate}>
                  Save new price
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {isSuperuser && !addingFresh && (
        <button className="boq-action-btn" style={{ fontSize: 10, padding: "2px 8px", marginTop: 4 }}
          onClick={() => setAddingFresh(true)} disabled={busy}>
          + add new supplier offer
        </button>
      )}
      {addingFresh && (
        <div style={{ marginTop: 6, padding: 6, border: "1px dashed #334155", borderRadius: 3, display: "grid", gap: 4 }}>
          <div style={{ fontSize: 10, color: "#94a3b8" }}>New supplier offer (different supplier than above):</div>
          <SearchableSelect
            options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
            value={freshOffer.supplier_id}
            onChange={(v) => setFreshOffer({ ...freshOffer, supplier_id: v })}
            placeholder="pick supplier..."
          />
          <div style={{ display: "flex", gap: 4 }}>
            <input type="number" placeholder="rate" value={freshOffer.unit_rate}
              onChange={(e) => setFreshOffer({ ...freshOffer, unit_rate: e.target.value })} style={{ flex: 1 }} />
            <input value={freshOffer.currency}
              onChange={(e) => setFreshOffer({ ...freshOffer, currency: e.target.value })}
              style={{ width: 56 }} placeholder="GEL" />
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button className="boq-action-btn" style={{ flex: 1, fontSize: 11 }}
              onClick={() => setAddingFresh(false)} disabled={busy}>Cancel</button>
            <button className="primary" style={{ flex: 2, fontSize: 11 }}
              onClick={handleAddFresh} disabled={busy || !freshOffer.supplier_id || !freshOffer.unit_rate}>
              Add offer
            </button>
          </div>
        </div>
      )}

      {past.length > 0 && (
        <>
          <button onClick={() => setShowHistory(!showHistory)}
            style={{
              background: "none", border: "none", color: "#94a3b8",
              cursor: "pointer", fontSize: 10, padding: "4px 0", marginTop: 4,
            }}>
            {showHistory ? "▾" : "▸"} Price history ({past.length})
          </button>
          {showHistory && (
            <div style={{ marginLeft: 8, marginTop: 2 }}>
              {past.map((o) => (
                <div key={o.id} style={{ fontSize: 10, color: "#94a3b8", padding: "2px 0", borderTop: "1px dotted #334155" }}>
                  <strong>{o.supplier_name}</strong>
                  <span style={{ marginLeft: 6 }}>{o.unit_rate} {o.currency}</span>
                  <span style={{ marginLeft: 6, color: "#64748b" }}>
                    {o.valid_from}{o.valid_to ? ` → ${o.valid_to}` : ""}
                  </span>
                  {o.note && <span style={{ marginLeft: 6, fontStyle: "italic" }}>· {o.note}</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Copy-from panel ─────────────────────────────────────────────────────────

function CopyFromPanel({ token, targetBaseWork, tier, onClose, onCopied }) {
  const [catalogs, setCatalogs] = useState([]);
  const [catalogId, setCatalogId] = useState("");
  const [baseWorks, setBaseWorks] = useState([]);
  const [bwSearch, setBwSearch] = useState("");
  const [selectedBw, setSelectedBw] = useState(null);
  const [sourceReqs, setSourceReqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState("");

  // load catalogs once
  useEffect(() => {
    fetchTier1Catalogs(token, tier)
      .then((list) => { setCatalogs(list || []); if (list?.length) setCatalogId(String(list[0].id)); })
      .catch(() => setError("Failed to load catalogs"));
  }, [token, tier]);

  // load base works when catalog changes
  useEffect(() => {
    if (!catalogId) return;
    setSelectedBw(null); setSourceReqs([]); setBaseWorks([]);
    setLoading(true);
    fetchTier1BaseWorks(token, catalogId, { limit: 2000, tier })
      .then((list) => setBaseWorks(list || []))
      .catch(() => setError("Failed to load base works"))
      .finally(() => setLoading(false));
  }, [token, catalogId, tier]);

  // load requirements when a base work is selected
  useEffect(() => {
    if (!selectedBw) { setSourceReqs([]); return; }
    setLoading(true);
    fetchBaseWorkRequirements(token, selectedBw.id, tier)
      .then((list) => setSourceReqs(list || []))
      .catch(() => setError("Failed to load requirements"))
      .finally(() => setLoading(false));
  }, [token, selectedBw, tier]);

  const filteredBws = bwSearch.trim()
    ? baseWorks.filter((bw) => {
        const q = bwSearch.toLowerCase();
        return (bw.code || "").toLowerCase().includes(q)
          || (bw.name_geo || "").toLowerCase().includes(q)
          || (bw.name_eng || "").toLowerCase().includes(q);
      })
    : baseWorks;

  const handleCopy = async () => {
    if (!sourceReqs.length) return;
    setCopying(true); setError("");
    let failed = 0;
    for (const req of sourceReqs) {
      try {
        await createBaseWorkRequirement(token, targetBaseWork.id, {
          requirement_kind: req.requirement_kind,
          classification_code: req.classification_code || null,
          product_id: req.product_id || null,
          quantity_per_base_unit: req.quantity_per_base_unit,
          unit_code: req.unit_code || null,
          note: req.note || null,
        }, tier);
      } catch {
        failed += 1;
      }
    }
    setCopying(false);
    if (failed > 0) setError(`${failed} requirement(s) failed to copy. Rest were copied.`);
    else onCopied();
  };

  const bwLabel = (bw) =>
    `${bw.code || ""}  —  ${bw.name_geo || bw.name_eng || bw.description_geo || ""}`.trim();

  return (
    <div style={{
      position: "absolute", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 10,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      borderRadius: 6,
    }}>
      <div style={{
        background: "#0f172a", border: "1px solid #14b8a6", borderRadius: 6,
        padding: 16, width: "100%", maxWidth: 580, maxHeight: "80vh",
        overflow: "auto", display: "flex", flexDirection: "column", gap: 10,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong style={{ fontSize: 13 }}>Copy requirements from another base work</strong>
          <button className="boq-action-btn" onClick={onClose} disabled={copying}>✕</button>
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>
          Target: <span style={{ color: "#e2e8f0", fontFamily: "monospace" }}>{targetBaseWork.code}</span>
          {" · "}{targetBaseWork.name_geo || targetBaseWork.name_eng || ""}
        </div>

        {error && <div className="error" style={{ fontSize: 11 }}>{error}</div>}

        {/* Catalog selector */}
        <div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 3 }}>Catalog</div>
          <select value={catalogId} onChange={(e) => setCatalogId(e.target.value)}
            style={{ width: "100%", fontSize: 12 }}>
            {catalogs.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Base work search + list */}
        <div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 3 }}>
            Search base work {loading && <span style={{ color: "#64748b" }}>— loading…</span>}
          </div>
          <input
            placeholder="filter by code or name…"
            value={bwSearch}
            onChange={(e) => setBwSearch(e.target.value)}
            style={{ width: "100%", fontSize: 12, marginBottom: 4 }}
          />
          <div style={{
            maxHeight: 180, overflowY: "auto", border: "1px solid #334155",
            borderRadius: 3, background: "#0a1628",
          }}>
            {filteredBws.length === 0 && (
              <div style={{ padding: 8, fontSize: 11, color: "#64748b" }}>
                {loading ? "Loading…" : "No base works found."}
              </div>
            )}
            {filteredBws.map((bw) => (
              <div key={bw.id}
                onClick={() => setSelectedBw(bw)}
                style={{
                  padding: "5px 8px", fontSize: 11, cursor: "pointer",
                  background: selectedBw?.id === bw.id ? "rgba(20,184,166,0.15)" : "transparent",
                  borderBottom: "1px dotted #1e293b",
                  display: "flex", gap: 6,
                }}>
                <span style={{ fontFamily: "monospace", color: "#67e8f9", minWidth: 80 }}>
                  {bw.code}
                </span>
                <span style={{ color: "#e2e8f0" }}>
                  {bw.name_geo || bw.name_eng || bw.description_geo || "—"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Preview requirements of selected base work */}
        {selectedBw && (
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
              Requirements in <span style={{ fontFamily: "monospace", color: "#67e8f9" }}>{selectedBw.code}</span>
              {loading && <span style={{ color: "#64748b" }}> — loading…</span>}
              {!loading && <span style={{ marginLeft: 4, color: "#94a3b8" }}>({sourceReqs.length})</span>}
            </div>
            <div style={{
              maxHeight: 200, overflowY: "auto", border: "1px solid #334155",
              borderRadius: 3, background: "#0a1628",
            }}>
              {sourceReqs.length === 0 && !loading && (
                <div style={{ padding: 8, fontSize: 11, color: "#64748b" }}>No requirements.</div>
              )}
              {sourceReqs.map((r) => {
                const name = r.product_id
                  ? (r.product_name_geo || r.product_name_eng || r.product_display_id || `#${r.product_id}`)
                  : (r.classification_name_geo || r.classification_name_eng || r.classification_code);
                const kindColor = KIND_COLOR[r.requirement_kind] || "#94a3b8";
                return (
                  <div key={r.id} style={{
                    display: "flex", alignItems: "center", padding: "4px 8px",
                    fontSize: 11, borderBottom: "1px dotted #1e293b", gap: 6,
                  }}>
                    <span style={{
                      background: kindColor, color: "#fff", fontSize: 9,
                      padding: "1px 5px", borderRadius: 3, fontWeight: 700, flexShrink: 0,
                    }}>{KIND_LETTER[r.requirement_kind]}</span>
                    <span style={{ flex: 1, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {name}
                    </span>
                    <span style={{ fontWeight: 600, color: "#f0abfc", flexShrink: 0 }}>
                      {r.quantity_per_base_unit} <span style={{ color: "#94a3b8" }}>{r.unit_code || ""}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="boq-action-btn" onClick={onClose} disabled={copying}>Cancel</button>
          <button className="primary" disabled={copying || !selectedBw || sourceReqs.length === 0}
            onClick={handleCopy}>
            {copying ? "Copying…" : `Copy ${sourceReqs.length} requirement${sourceReqs.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequirementEditForm({ token, baseWorkId, tier, units, existingReq, onCancel, onSaved, setError }) {
  const isNew = !existingReq;
  const [kind, setKind] = useState(existingReq?.requirement_kind || "MATERIAL");
  const [refMode, setRefMode] = useState(
    existingReq?.product_id ? "product" : "classification"
  );
  const [classificationCode, setClassificationCode] = useState(existingReq?.classification_code || "");
  const [productId, setProductId] = useState(existingReq?.product_id ? String(existingReq.product_id) : "");
  const [quantity, setQuantity] = useState(existingReq?.quantity_per_base_unit ?? 0);
  const [unitCode, setUnitCode] = useState(existingReq?.unit_code || existingReq?.product_base_unit || "");
  const [note, setNote] = useState(existingReq?.note || "");

  const [classifications, setClassifications] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchProduct, setSearchProduct] = useState("");
  const [saving, setSaving] = useState(false);

  // Load classifications (flat) when kind changes
  useEffect(() => {
    fetchClassifications(token, { kind, parentCode: "__all__" })
      .then((list) => setClassifications(list || []))
      .catch(() => setClassifications([]));
  }, [token, kind]);

  // Load products (debounced) when kind / search change
  useEffect(() => {
    const t = setTimeout(() => {
      fetchProducts(token, { kind, q: searchProduct, limit: 100 })
        .then((list) => setProducts(list || []))
        .catch(() => setProducts([]));
    }, 200);
    return () => clearTimeout(t);
  }, [token, kind, searchProduct]);

  const classificationOptions = useMemo(
    () => classifications.map((c) => ({
      value: c.code,
      label: `${c.code}  —  L${c.level}  ${c.name_geo || c.name_eng || ""}`,
    })),
    [classifications]
  );
  const productOptions = useMemo(
    () => products.map((p) => ({
      value: String(p.id),
      label: `${p.display_id || `#${p.id}`}  —  ${p.name_geo || p.name_eng || ""}${p.is_generic ? " (generic)" : ""}`,
    })),
    [products]
  );
  const unitOptions = useMemo(
    () => (units || []).map((u) => ({
      value: u.code,
      label: `${u.code}  —  ${u.symbol || ""} (${u.dimension})`,
    })),
    [units]
  );

  const canSave =
    Boolean(quantity !== "" && Number(quantity) > 0) &&
    (refMode === "classification" ? Boolean(classificationCode) : Boolean(productId));

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        requirement_kind: kind,
        classification_code: refMode === "classification" ? classificationCode : null,
        product_id: refMode === "product" ? Number(productId) : null,
        quantity_per_base_unit: Number(quantity) || 0,
        unit_code: unitCode || null,
        note: note || null,
      };
      if (isNew) {
        await createBaseWorkRequirement(token, baseWorkId, payload, tier);
      } else {
        await updateBaseWorkRequirement(token, baseWorkId, existingReq.id, payload, tier);
      }
      onSaved();
    } catch (e) {
      setError(e?.message || "Failed to save requirement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel" style={{ marginTop: 10, padding: 10, border: "1px solid #14b8a6" }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
        {isNew ? "New requirement" : `Edit requirement #${existingReq.id}`}
      </div>
      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>
        <span style={{ color: "#f87171" }}>*</span> required field
      </div>

      {/* Kind */}
      <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
        {KINDS.map((k) => (
          <button
            key={k.id}
            onClick={() => setKind(k.id)}
            className={`sheet-lookup-chip ${kind === k.id ? "active" : ""}`}
            style={{ borderColor: k.color }}
          >
            {k.label}
          </button>
        ))}
      </div>

      {/* Reference mode toggle */}
      <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
        <button
          onClick={() => setRefMode("classification")}
          className={`sheet-lookup-chip ${refMode === "classification" ? "active" : ""}`}
          style={{ flex: 1 }}
        >
          Generic (classification)
        </button>
        <button
          onClick={() => setRefMode("product")}
          className={`sheet-lookup-chip ${refMode === "product" ? "active" : ""}`}
          style={{ flex: 1 }}
        >
          Specific (product)
        </button>
      </div>

      {refMode === "classification" ? (
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, marginBottom: 6, fontSize: 11 }}>
          <span style={{ color: "#e2e8f0" }}>Classification <span style={{ color: "#f87171" }}>*</span></span>
          <SearchableSelect
            options={classificationOptions}
            value={classificationCode}
            onChange={(v) => setClassificationCode(v)}
            placeholder="search by code or name..."
          />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 6 }}>
            <input
              placeholder="filter products..."
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              style={{ width: "100%", fontSize: 11 }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, marginBottom: 6, fontSize: 11 }}>
            <span style={{ color: "#e2e8f0" }}>Product <span style={{ color: "#f87171" }}>*</span></span>
            <SearchableSelect
              options={productOptions}
              value={productId}
              onChange={(v) => setProductId(v)}
              placeholder="pick product..."
            />
          </div>
        </>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, marginBottom: 6, fontSize: 11 }}>
        <span style={{ color: "#e2e8f0" }}>Quantity / base unit <span style={{ color: "#f87171" }}>*</span></span>
        <input
          type="number" step="0.0001"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="e.g. 441 for 441 man-h per 100 m³"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, marginBottom: 6, fontSize: 11 }}>
        <span style={{ color: "#94a3b8" }}>Unit</span>
        <SearchableSelect
          options={unitOptions}
          value={unitCode}
          onChange={(v) => setUnitCode(v)}
          placeholder="defaults to product's base unit if blank"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, marginBottom: 8, fontSize: 11 }}>
        <span style={{ color: "#94a3b8" }}>Note</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 'or equivalent grade'" />
      </div>

      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button className="boq-action-btn" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="primary" onClick={handleSave} disabled={saving || !canSave}>
          {saving ? "…" : isNew ? "Add requirement" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
